import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import type { GatewayKeyGuardrailUpdateItem } from "@/lib/http/gatewaykeys";
import { api } from "@/lib/http";

export const gatewayKeyEntitySchema = z.object({
  id: z.number(),
  name: z.string(),
  policy_mode: z.string(),
});

export const gatewayKeyItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(["active", "revoked"]),
  revoked_at: z.iso.datetime().nullable(),
  last_used_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  providers: z.record(z.string(), z.array(z.string())),
  entities: z.array(gatewayKeyEntitySchema),
});

export const gatewayKeysListSchema = z.object({
  count: z.number(),
  items: z.array(gatewayKeyItemSchema),
  next_page: z.number().nullable(),
  previous_page: z.number().nullable(),
});

export type GatewayKeyEntity = z.infer<typeof gatewayKeyEntitySchema>;
export type GatewayKeyItem = z.infer<typeof gatewayKeyItemSchema>;
export type GatewayKeysList = z.infer<typeof gatewayKeysListSchema>;

export function gatewayKeysListQueryOptions(
  status: "active" | "revoked",
  page: number,
  perPage = 10,
) {
  return queryOptions({
    queryKey: ["gateway-keys", "list", status, page, perPage],
    queryFn: () =>
      status === "active"
        ? api.gatewaykeys.listActive(page, perPage)
        : api.gatewaykeys.listRevoked(page, perPage),
  });
}

export function useGatewayKeysList(
  status: "active" | "revoked",
  page: number,
  perPage = 10,
) {
  return useQuery(gatewayKeysListQueryOptions(status, page, perPage));
}

export function useRevokeGatewayKey(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.gatewaykeys.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gateway-keys"] });
      options?.onSuccess?.();
    },
  });
}

export function useRenameGatewayKey(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.gatewaykeys.rename(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gateway-keys"] });
      options?.onSuccess?.();
    },
  });
}

export function gatewayKeyGuardrailsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["gateway-keys", id, "guardrails"],
    queryFn: () => api.gatewaykeys.getGuardrails(id),
  });
}

export function useGatewayKeyGuardrails(id: string, enabled = true) {
  return useQuery({
    ...gatewayKeyGuardrailsQueryOptions(id),
    enabled: enabled && id.length > 0,
  });
}

export function useUpdateGatewayKeyGuardrails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      items,
    }: {
      id: string;
      items: Array<GatewayKeyGuardrailUpdateItem>;
    }) => api.gatewaykeys.updateGuardrails(id, items),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ["gateway-keys", id, "guardrails"],
      });
    },
  });
}
